import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Loader, Modal, Stack, Text } from '@mantine/core';
import { useElementSize, useMediaQuery, useViewportSize } from '@mantine/hooks';
import { HUBSPOT_MEETINGS_ORIGINS } from '@/PageComponents/Onboarding/onboarding-config';
import { buildMeetingEmbedUrl, fitMeetingFrame, MeetingBooking, meetingModalWidth, parseMeetingBookedMessage, parseMeetingHeightMessage } from '@/PageComponents/Onboarding/hubspot-booking';
import { BookDemoLocation, ONBOARDING_EVENTS, trackOnboarding, useOnboardingModalAnalytics } from '@/PageComponents/Onboarding/onboarding-analytics';

// Phones go full screen and use HubSpot's phone layout (stretched to the screen). Wider screens keep
// the modal and HubSpot's desktop layout, scaled down to fit when space is short. See fitMeetingFrame.
const FULL_SCREEN_QUERY = '(max-width: 480px)';
// Mantine's "xl" modal width, used whenever the scheduler fits at full size.
const MODAL_WIDTH = 780;
// Mantine's modal title row.
const MODAL_HEADER_HEIGHT = 60;
// Gap between the modal and the top/bottom of the screen (Mantine's yOffset).
const MODAL_Y_OFFSET = 16;
// HubSpot posts its first height once the scheduler has rendered (~1s after the iframe loads).
// If that never arrives, stop covering the frame after this long anyway.
const READY_FALLBACK_MS = 6000;

export interface BookDemoModalProps {
    opened: boolean;
    onClose: () => void;
    onBooked: (booking: MeetingBooking) => void;
    /** The "Book a demo" button that opened it, for analytics. */
    location: BookDemoLocation;
    meetingUrl: string;
    user: { firstName?: string; lastName?: string; email?: string };
}

const BookDemoModal: FC<BookDemoModalProps> = ({ opened, onClose, onBooked, location, meetingUrl, user }) => {
    // Its opening is the Book a demo click (onboarding_book_demo_clicked), so only the close is recorded here.
    const setOutcome = useOnboardingModalAnalytics('book_demo', opened, { location }, { trackOpen: false });
    const locationRef = useRef(location);
    locationRef.current = location;
    const isMobile = useMediaQuery(FULL_SCREEN_QUERY);
    const embedUrl = useMemo(() => buildMeetingEmbedUrl(meetingUrl, user), [meetingUrl, user.firstName, user.lastName, user.email]);

    // Keep the latest callback without re-binding the listener on every parent render.
    const onBookedRef = useRef(onBooked);
    onBookedRef.current = onBooked;

    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [contentHeight, setContentHeight] = useState<number | null>(null);
    // Until HubSpot has drawn its scheduler, cover the frame with our own full-height loader
    // (its built-in spinner sits squashed at the top of the frame).
    const [ready, setReady] = useState(false);
    const [frameLoaded, setFrameLoaded] = useState(false);

    // A fresh open loads a fresh iframe, so show the loader again.
    useEffect(() => {
        if (opened) return;
        setReady(false);
        setFrameLoaded(false);
    }, [opened]);

    // Size the scheduler to the space the modal really has, so neither the modal nor the frame scrolls.
    const { ref: boxRef, width: boxWidth } = useElementSize();
    const { height: viewportHeight } = useViewportSize();
    const availableHeight = Math.max(0, viewportHeight - MODAL_HEADER_HEIGHT - (isMobile ? 0 : MODAL_Y_OFFSET * 2));
    // On phones HubSpot under-reports its height; the phone layout fills the frame anyway.
    const reportedHeight = ready && !isMobile ? contentHeight : null;
    const fit = fitMeetingFrame({ width: boxWidth, availableHeight, phone: !!isMobile, reportedHeight });
    const modalWidth = meetingModalWidth({ preferredWidth: MODAL_WIDTH, availableHeight, reportedHeight });

    // The site's chat launcher sits over HubSpot's Confirm button (full screen, and on shorter
    // screens in the modal too). Hide it while the modal is open (same approach as sign-up-icp).
    useEffect(() => {
        if (!opened) return;
        const setChatDisplay = (display: string) =>
            document.getElementById('hubspot-messages-iframe-container')?.style.setProperty('display', display, 'important');
        setChatDisplay('none');
        const interval = window.setInterval(() => setChatDisplay('none'), 200);
        return () => {
            window.clearInterval(interval);
            setChatDisplay('block');
        };
    }, [opened]);

    useEffect(() => {
        if (!frameLoaded || ready) return;
        const timer = window.setTimeout(() => setReady(true), READY_FALLBACK_MS);
        return () => window.clearTimeout(timer);
    }, [frameLoaded, ready]);

    useEffect(() => {
        if (!opened) return;
        const listener = (event: MessageEvent) => {
            // When our iframe is mounted, only accept messages that came from it.
            const frame = iframeRef.current;
            if (frame && event.source !== frame.contentWindow) return;
            const height = frame ? parseMeetingHeightMessage(event, HUBSPOT_MEETINGS_ORIGINS) : null;
            if (height) {
                setContentHeight(height);
                setReady(true);
                return;
            }
            const booking = parseMeetingBookedMessage(event, HUBSPOT_MEETINGS_ORIGINS);
            if (booking) {
                setOutcome('booked');
                trackOnboarding(ONBOARDING_EVENTS.demoBooked, { location: locationRef.current, demo_start: booking.startISO }, {
                    onboarding_demo_booked_at: new Date().toISOString(),
                    onboarding_demo_booked_from: locationRef.current,
                });
                onBookedRef.current(booking);
            }
        };
        window.addEventListener('message', listener);
        return () => window.removeEventListener('message', listener);
    }, [opened]);

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title="Book a demo"
            withCloseButton
            closeButtonProps={{ 'aria-label': 'Close', id: 'onboarding-book-demo-close' }}
            size={embedUrl ? modalWidth : 'xl'}
            fullScreen={isMobile}
            centered
            yOffset={MODAL_Y_OFFSET}
            // The scheduler brings its own padding and footer; let it run edge to edge.
            // The scheduler is sized to fit, so the modal never needs its own scrollbar: hide it, or a
            // pixel of rounding (zoom, title-row height) shows one. Only a too-short screen still scrolls.
            styles={{
                // A heading, not body text: matches the dashboard's section titles.
                title: { fontSize: 20, fontWeight: 700, color: '#2b3136' },
                body: { padding: 0 },
                content: embedUrl && fit.fitsHeight ? { overflowY: 'hidden' } : undefined,
            }}
        >
            {embedUrl ? (
                <Box
                    ref={boxRef}
                    data-testid="book-demo-frame-box"
                    pos="relative"
                    // Cropped to HubSpot's card: no blank margins or footer, and nothing to scroll.
                    style={{ height: `${fit.boxHeight}px`, overflow: 'hidden' }}
                >
                    <iframe
                        ref={iframeRef}
                        title="Book a demo with ServCraft"
                        src={embedUrl}
                        onLoad={() => setFrameLoaded(true)}
                        scrolling="no"
                        style={{
                            border: 0,
                            display: 'block',
                            position: 'absolute',
                            top: fit.top,
                            left: fit.left,
                            width: fit.frameWidth ? `${fit.frameWidth}px` : '100%',
                            height: `${fit.frameHeight}px`,
                            transform: fit.scale === 1 ? undefined : `scale(${fit.scale})`,
                            transformOrigin: '0 0',
                        }}
                    />
                    {!ready && (
                        <Stack
                            data-testid="book-demo-loader"
                            pos="absolute"
                            inset={0}
                            bg="white"
                            align="center"
                            justify="center"
                            gap="sm"
                            role="status"
                        >
                            <Loader color="scBlue" />
                            <Text size="sm" c="dimmed">Loading available times…</Text>
                        </Stack>
                    )}
                </Box>
            ) : (
                <Stack gap="xs" py="xl" px="md" align="center">
                    <Text fw={600}>Online booking will be available soon.</Text>
                    <Text size="sm" c="dimmed" ta="center">In the meantime, start a chat with us from the help menu and we'll set up your demo.</Text>
                </Stack>
            )}
        </Modal>
    );
};

export default BookDemoModal;
